import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Clock, MapPin, Download, Info, CalendarClock, Search, Filter,
  Store, Handshake, SprayCan, GraduationCap, Users, ClipboardCheck,
  ChefHat, BookOpen, UserCog, Wrench, LayoutList, Map as MapIcon, Calendar as CalIcon,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { downloadCsv } from "@/lib/csv";
import { useAuth } from "@/lib/auth";
import ActivityFormModal from "@/components/activities/ActivityFormModal";
import ActivityDetailModal from "@/components/activities/ActivityDetailModal";
import RegionMap from "@/components/RegionMap";
import { ACTIVITY_TYPES } from "@/lib/activityTypes";

const ICONS = { Store, Handshake, SprayCan, GraduationCap, Users, ClipboardCheck, ChefHat, BookOpen, UserCog, Wrench };
const typeByKey = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.key, t]));

export default function Activities() {
  const { user } = useAuth();
  const canExport = user?.role === "admin" || user?.role === "director";
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("list"); // list | calendar | map

  // Filters
  const [f, setF] = useState({ q: "", type: "all", dr: "all", user_id: "all", sector: "all", store: "all",
                               date_from: "", date_to: "" });

  const load = async () => {
    const [{ data: a }, { data: s }, { data: sec }, { data: u }] = await Promise.all([
      api.get("/activities"), api.get("/stores"), api.get("/sectors"), api.get("/users"),
    ]);
    setRows(a); setStores(s); setSectors(sec); setUsers(u);
  };
  useEffect(() => { load(); }, []);

  const storesById = useMemo(() => Object.fromEntries(stores.map(s => [s.id, s])), [stores]);
  const sectorsById = useMemo(() => Object.fromEntries(sectors.map(s => [s.id, s])), [sectors]);
  const usersById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const drs = useMemo(() => users.filter(u => u.role === "director"), [users]);
  const bms = useMemo(() => users.filter(u => u.role === "manager"), [users]);

  const storeName = (id) => storesById[id]?.name || "—";
  const sectorName = (id) => sectorsById[id]?.name || "—";

  const filtered = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    return rows.filter(r => {
      if (f.type !== "all" && r.type_key !== f.type) return false;
      if (f.sector !== "all" && r.sector_id !== f.sector) return false;
      if (f.store !== "all" && r.store_id !== f.store) return false;
      if (f.user_id !== "all" && r.user_id !== f.user_id) return false;
      if (f.dr !== "all") {
        // Match: activity's sector's DR
        const st = storesById[r.store_id]; const sec = sectorsById[r.sector_id];
        const drUser = drs.find(d => d.id === f.dr);
        if (!drUser) return false;
        const drSectors = drUser.sectors || [];
        if (!drSectors.includes(r.sector_id) && !(st && drSectors.includes(st.sector_id))) return false;
      }
      if (f.date_from && r.date < f.date_from) return false;
      if (f.date_to && r.date > f.date_to) return false;
      if (!q) return true;
      return [r.visit_type, r.description, r.user_name, storeName(r.store_id)]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [rows, f, storesById, sectorsById, drs]);

  const resetFilters = () => setF({ q: "", type: "all", dr: "all", user_id: "all", sector: "all", store: "all", date_from: "", date_to: "" });
  const activeFilters = Object.values(f).filter(v => v && v !== "all").length;

  // Prepare map points: unique stores from filtered activities + count
  const mapPoints = useMemo(() => {
    const counts = {};
    filtered.forEach(r => { if (r.store_id) counts[r.store_id] = (counts[r.store_id] || 0) + 1; });
    return Object.entries(counts).map(([sid, n]) => {
      const st = storesById[sid]; if (!st?.lat || !st?.lng) return null;
      return { ...st, perf: null, status: n >= 3 ? "ok" : n >= 1 ? "watch" : "crit", renew_badge: n > 1 ? `${n}` : null };
    }).filter(Boolean);
  }, [filtered, storesById]);

  // Calendar view (month grid)
  const [monthOffset, setMonthOffset] = useState(0);
  const monthStart = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth() + monthOffset); d.setDate(1); d.setHours(0,0,0,0); return d; }, [monthOffset]);
  const monthLabel = monthStart.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  const firstDay = (monthStart.getDay() + 6) % 7; // Monday-first
  const grid = [...Array(firstDay).fill(null), ...Array(daysInMonth).fill(0).map((_, i) => i + 1)];
  while (grid.length % 7 !== 0) grid.push(null);

  const activitiesByDate = useMemo(() => {
    const m = {}; filtered.forEach(r => { if (!m[r.date]) m[r.date] = []; m[r.date].push(r); });
    return m;
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="activities-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Actions terrain</h1>
          <p className="text-slate-500 mt-1">Toutes vos visites, formations et interventions en un fil.</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <button data-testid="activity-export" onClick={() => downloadCsv("/export/activities.csv", "activites.csv")}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm">
              <Download className="w-4 h-4"/> Export CSV
            </button>
          )}
          <button data-testid="activity-new" onClick={() => setOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm">
            <Plus className="w-4 h-4"/> Nouvelle activité
          </button>
        </div>
      </div>

      {/* Quick-launch tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {ACTIVITY_TYPES.filter(t => !t.roles || t.roles.includes(user?.role)).slice(0, 10).map(t => {
          const Icon = ICONS[t.icon] || Store;
          return (
            <button key={t.key} data-testid={`activity-quick-${t.key}`} onClick={() => setOpen(true)}
                    className="text-left rounded-xl bg-white border border-slate-200 p-3 hover:border-blue-400 hover:shadow-md transition flex flex-col gap-2 min-h-[86px]">
              <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `${t.color}18`, color: t.color }}>
                <Icon className="w-4 h-4"/>
              </div>
              <div className="text-xs font-semibold text-slate-900 leading-tight">{t.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={f.q} onChange={e => setF({...f, q: e.target.value})} data-testid="activity-search"
                   placeholder="Rechercher…"
                   className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-blue-100"/>
          </div>
          {/* View switcher */}
          <div className="flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
            {[{v:"list",Icon:LayoutList,label:"Liste"},{v:"calendar",Icon:CalIcon,label:"Calendrier"},{v:"map",Icon:MapIcon,label:"Carte"}].map(o => (
              <button key={o.v} data-testid={`activity-view-${o.v}`} onClick={() => setView(o.v)}
                      className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 transition ${view === o.v ? "bg-white text-blue-600" : "text-slate-500 hover:text-slate-800"}`}>
                <o.Icon className="w-3.5 h-3.5"/> {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={f.type} onChange={e => setF({...f, type: e.target.value})} data-testid="filter-type"
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Toutes activités</option>
            {ACTIVITY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={f.dr} onChange={e => setF({...f, dr: e.target.value})} data-testid="filter-dr"
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Tous les DR</option>
            {drs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={f.user_id} onChange={e => setF({...f, user_id: e.target.value})} data-testid="filter-user"
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Tous responsables</option>
            {[...bms, ...users.filter(u => ["rt","mm"].includes(u.role))].map(u =>
              <option key={u.id} value={u.id}>{u.name} · {u.role.toUpperCase()}</option>
            )}
          </select>
          <select value={f.sector} onChange={e => setF({...f, sector: e.target.value})} data-testid="filter-sector"
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Tous secteurs</option>
            {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={f.store} onChange={e => setF({...f, store: e.target.value})} data-testid="filter-store"
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs max-w-[220px]">
            <option value="all">Tous corners</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={f.date_from} onChange={e => setF({...f, date_from: e.target.value})} data-testid="filter-from"
                 className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs"/>
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={f.date_to} onChange={e => setF({...f, date_to: e.target.value})} data-testid="filter-to"
                 className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs"/>
          {activeFilters > 0 && (
            <button onClick={resetFilters} data-testid="filter-reset" className="text-xs text-rose-600 hover:text-rose-800 font-semibold">
              Réinitialiser ({activeFilters})
            </button>
          )}
          <span className="ml-auto text-xs text-slate-500 font-semibold">{filtered.length} résultat(s)</span>
        </div>
      </div>

      {/* Views */}
      {view === "list" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="divide-y divide-slate-100">
            {filtered.map(r => {
              const meta = typeByKey[r.type_key] || { color: "#3b82f6", icon: "Store", label: r.visit_type };
              const Icon = ICONS[meta.icon] || Store;
              return (
                <button key={r.id} data-testid={`activity-row-${r.id}`} onClick={() => setDetail(r)}
                     className={`w-full text-left p-4 sm:p-5 hover:bg-slate-50 transition flex gap-3 sm:gap-4 items-start ${r.info_important ? "bg-amber-50/50" : ""}`}>
                  <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${meta.color}18`, color: meta.color }}>
                    <Icon className="w-5 h-5"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">{meta.label}</span>
                      {r.info_important && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wider">
                          <Info className="w-3 h-3"/> Important
                        </span>
                      )}
                      <span className="text-xs text-slate-500">· {new Date(r.date).toLocaleDateString("fr-FR")}</span>
                      <span className="text-xs text-slate-400">· {r.user_name}</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-1 line-clamp-2">{r.description}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      {r.store_id && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/> {storeName(r.store_id)}</span>}
                      {r.sector_id && <span className="inline-flex items-center gap-1">· {sectorName(r.sector_id)}</span>}
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/> {r.duration_min} min</span>
                      {r.next_visit_date && (
                        <span className="inline-flex items-center gap-1 text-blue-600 font-semibold">
                          <CalendarClock className="w-3 h-3"/> Prochaine : {new Date(r.next_visit_date).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                      {r.payload?.score_pct != null && (
                        <span className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full ${
                          r.payload.score_pct >= 85 ? "bg-emerald-100 text-emerald-700" : r.payload.score_pct >= 65 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"
                        }`}>Conformité {r.payload.score_pct}%</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-center py-16 text-slate-400">
                <ClipboardCheck className="w-8 h-8 mx-auto mb-2 text-slate-300"/>
                Aucune activité pour ces filtres.
              </div>
            )}
          </div>
        </div>
      )}

      {view === "calendar" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <button data-testid="cal-prev" onClick={() => setMonthOffset(m => m - 1)} className="w-9 h-9 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4"/></button>
            <div className="font-display text-lg font-semibold text-slate-800 capitalize">{monthLabel}</div>
            <button data-testid="cal-next" onClick={() => setMonthOffset(m => m + 1)} className="w-9 h-9 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight className="w-4 h-4"/></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs">
            {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => (
              <div key={d} className="p-2 text-center font-semibold text-slate-400 uppercase">{d}</div>
            ))}
            {grid.map((day, i) => {
              if (day == null) return <div key={i}/>;
              const iso = new Date(monthStart.getFullYear(), monthStart.getMonth(), day).toISOString().slice(0,10);
              const evs = activitiesByDate[iso] || [];
              const isToday = iso === new Date().toISOString().slice(0,10);
              return (
                <div key={i} className={`min-h-[80px] p-1.5 rounded-lg border ${isToday ? "bg-blue-50 border-blue-300" : "bg-slate-50 border-slate-100"}`}>
                  <div className={`text-[11px] font-bold ${isToday ? "text-blue-600" : "text-slate-500"}`}>{day}</div>
                  <div className="mt-1 space-y-0.5">
                    {evs.slice(0, 3).map(e => {
                      const meta = typeByKey[e.type_key] || { color: "#3b82f6" };
                      return (
                        <button key={e.id} onClick={() => setDetail(e)} data-testid={`cal-ev-${e.id}`}
                                className="w-full text-left text-[10px] px-1.5 py-0.5 rounded-md truncate font-semibold hover:brightness-95"
                                style={{ background: `${meta.color}22`, color: meta.color }}>
                          {(typeByKey[e.type_key]?.label || e.visit_type).slice(0, 22)}
                        </button>
                      );
                    })}
                    {evs.length > 3 && <div className="text-[10px] text-slate-400 pl-1">+{evs.length - 3} autre(s)</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "map" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-xs text-slate-500 mb-2">Corners avec activités sur la période filtrée · Cliquez sur une pastille pour voir le détail</div>
          <RegionMap stores={mapPoints} height={520}
                     onStoreClick={(s) => {
                       const first = filtered.find(r => r.store_id === s.id);
                       if (first) setDetail(first);
                     }}/>
        </div>
      )}

      <ActivityFormModal open={open} onClose={() => setOpen(false)} onSaved={load}
                         stores={stores} sectors={sectors} userRole={user?.role}/>
      <ActivityDetailModal activity={detail} onClose={() => setDetail(null)}
                           storeName={storeName} sectorName={sectorName}/>
    </div>
  );
}
